import * as React from 'react';
import { RouteComponentProps, useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import {
  useClonePlaceFaceMutation,
  useDeletePlaceFaceMutation,
  useGetPlaceFacesLazyQuery,
  useUpdatePlaceFacesDisplayOrderMutation,
} from '../generated/graphql';
import * as Const from '../Const';
import { Loading } from '../Loading';
import './PlaceDxfListPage.css';
import { AuthButton } from '../components/Button';
import { GraphQLErrorDialog, ConfirmDialog } from '../components/Dialogs';
import { EmptyRow } from '../components/EmptyRow';
import { SectionHeader } from '../components/SectionHeader';
import { ToastContext } from '../components/Toast';
import { formatDate } from '../utils/DateHelper';
import { downloadFile } from '../utils/DownloadFile';
import { TableScroll } from '../components/TableScroll';
import { orderByIds } from '../utils/reorder';
import {
  getReorderTableStorageKey,
  getTableColumnCount,
  ReorderHandleCell,
  ReorderHandleHeader,
  useListReorder,
} from '../components/ReorderMode';
import { ListSummary } from '../components/ListControls';
import { FileNameButton, FilePreviewWindow, isPreviewableFile } from '../components/FilePreviewWindow';

type IPlaceDxfListPageProps = RouteComponentProps<{ placeId: string }>;

/**
 * 表の列数。0件の行を表の幅いっぱいに広げるために使う。
 * 並びを変えられる人には左端に取っ手の列が加わるので、0件の行の幅は getTableColumnCount で数える
 */
const COLUMN_COUNT = 7;

/** 図面の編集に必要な権限の段階。編集・コピー・削除のボタン（AuthButton）と同じ段階にする */
const EDIT_AUTHORITY_LEVEL = 2;

function getByteLengthString(bytes: number) {
  if (bytes === 0) {
    return '0B';
  }

  const k = 1000; // not 1024 (https://physics.nist.gov/cuu/Units/binary.html)
  const dm = 2;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + sizes[i];
}

interface FaceTarget {
  id: number;
  dxf_file_name: string;
}

export const PlaceDxfListPage: React.FC<IPlaceDxfListPageProps> = function (
  props
) {
  const placeId = parseInt(props.match.params.placeId);
  const history = useHistory();
  const toastContext = React.useContext(ToastContext);
  // 拡大表示の窓で開いている図面
  const [previewFace, setPreviewFace] = React.useState<{ fileName: string; fileUrl: string }>();
  const [cloneFaceTarget, setCloneFaceTarget] = React.useState<FaceTarget>();
  const [deleteFaceTarget, setDeleteFaceTarget] = React.useState<FaceTarget>();
  const [cloneFace, cloneFaceResult] = useClonePlaceFaceMutation({
    onCompleted() {
      toastContext.showToast('コピーしました', 'success');
      getFaces();
    },
    onError() {
      toastContext.showToast('コピーに失敗しました', 'error');
    },
  });
  const [deleteFace, deleteFaceResult] = useDeletePlaceFaceMutation({
    onCompleted() {
      toastContext.showToast('削除しました', 'success');
      getFaces();
    },
    onError() {
      toastContext.showToast('削除に失敗しました', 'error');
    },
  });
  const [getFaces, getPlaceResult] = useGetPlaceFacesLazyQuery({
    variables: { placeId },
  });
  const [updateDisplayOrder] = useUpdatePlaceFacesDisplayOrderMutation();
  // データを読み込む前は空の並びを渡し、読み込んだ後はサーバーが返した並び順を渡す。
  // 取っ手を落とすとすぐに保存し、保存の通知と「元に戻す」はこのフックが出す
  const reorderMode = useListReorder((getPlaceResult.data?.getPlace.faces ?? []).map((face) => face.id), {
    authLv: EDIT_AUTHORITY_LEVEL,
    filtering: false,
    save: (ids) => updateDisplayOrder({ variables: { placeId, faceIds: ids } }),
  });

  if (!getPlaceResult.called) {
    getFaces();
  }

  if (getPlaceResult.error) {
    return <GraphQLErrorDialog error={getPlaceResult.error} />;
  }

  if (getPlaceResult.loading || !getPlaceResult.data) {
    return <Loading />;
  }

  const faces = getPlaceResult.data.getPlace.faces;
  const displayedFaces = orderByIds(faces, reorderMode.displayedIds);

  let rows;
  if (displayedFaces.length === 0) {
    rows = <EmptyRow colSpan={getTableColumnCount(reorderMode, COLUMN_COUNT)} message="図面が登録されていません" />;
  } else {
    rows = displayedFaces.map((face, index) => {
      const fileUrl = Const.API_URL + face.dxf_url;

      // ファイル名を押すと、画像の図面は拡大表示の窓を開き、DXFとPDFの図面はそのままダウンロードする
      const openFile = () => {
        if (isPreviewableFile(face.dxf_file_name)) {
          setPreviewFace({ fileName: face.dxf_file_name, fileUrl });
          return;
        }
        downloadFile(fileUrl, face.dxf_file_name);
      };

      // 並びを変えられる人には、行の左端に取っ手の列を置く。操作の列（編集・コピー・削除）はいつも出す
      let handleCell;
      if (reorderMode.handleColumn) {
        handleCell = <ReorderHandleCell {...reorderMode.getHandleCellProps(face.id, index)} />;
      }
      const fileNameContent = (
        <FileNameButton className="PlaceDxfListPage-file_link" fileName={face.dxf_file_name} onClick={openFile} />
      );
      const markerCountContent = (
        <Link to={`/places/${placeId}/faces/${face.id}/markers`}>
          {face.markers.length}
        </Link>
      );
      const pictureCountContent = (
        <Link to={`/places/${placeId}/faces/${face.id}/pictures`}>
          {face.pictures.length}
        </Link>
      );
      const actionCell = (
        <td className="App-table_button_cell">
          <div className="App-table_actions">
            <AuthButton
              authLv={EDIT_AUTHORITY_LEVEL}
              mini={true}
              color="white"
              onClick={() =>
                history.push(`/places/${placeId}/faces/${face.id}/edit`)
              }
            >
              編集
            </AuthButton>
            <AuthButton
              authLv={EDIT_AUTHORITY_LEVEL}
              mini={true}
              color="white"
              onClick={() => setCloneFaceTarget(face)}
            >
              コピー
            </AuthButton>
            <AuthButton
              authLv={EDIT_AUTHORITY_LEVEL}
              mini={true}
              color="red"
              onClick={() => setDeleteFaceTarget(face)}
            >
              削除
            </AuthButton>
          </div>
        </td>
      );

      return (
        <tr key={face.id} {...reorderMode.getRowProps(face.id)}>
          {handleCell}
          <td>{fileNameContent}</td>
          <td>{getByteLengthString(face.dxf_file_size)}</td>
          <td>{markerCountContent}</td>
          <td>{pictureCountContent}</td>
          <td>{formatDate(face.created)}</td>
          <td>{formatDate(face.updated)}</td>
          {actionCell}
        </tr>
      );
    });
  }

  // 見出しの右のボタン
  const headerActions = (
    <>
      <AuthButton
        authLv={EDIT_AUTHORITY_LEVEL}
        mini={true}
        onClick={() => history.push(`/places/${placeId}/faces/new`)}
      >
        新規作成
      </AuthButton>
    </>
  );

  // 並びを変えられる人には、取っ手の列を左端に置き、見出しは空にして幅を変えられないようにする
  let handleHeader;
  if (reorderMode.handleColumn) {
    handleHeader = <ReorderHandleHeader />;
  }
  const actionHeader = <th>操作</th>;

  const tableStorageKey = getReorderTableStorageKey(reorderMode, 'placeFaces');

  let cloneDialog;
  if (cloneFaceTarget) {
    cloneDialog = (
      <ConfirmDialog
        open={true}
        title="図面のコピー"
        message={<p>{cloneFaceTarget.dxf_file_name}をコピーしますか？</p>}
        yesLabel="コピー"
        onYes={() => {
          cloneFace({ variables: { placeFaceId: cloneFaceTarget.id } });
          setCloneFaceTarget(undefined);
        }}
        onNo={() => setCloneFaceTarget(undefined)}
      />
    );
  }

  let deleteDialog;
  if (deleteFaceTarget) {
    deleteDialog = (
      <ConfirmDialog
        open={true}
        danger={true}
        title="図面の削除"
        message={<p>{deleteFaceTarget.dxf_file_name}を削除しますか？</p>}
        yesLabel="削除"
        onYes={() => {
          deleteFace({ variables: { faceId: deleteFaceTarget.id } });
          setDeleteFaceTarget(undefined);
        }}
        onNo={() => setDeleteFaceTarget(undefined)}
      />
    );
  }

  let previewModal;
  if (previewFace) {
    previewModal = (
      <FilePreviewWindow
        fileName={previewFace.fileName}
        src={previewFace.fileUrl}
        onClose={() => setPreviewFace(undefined)}
        onDownload={() => downloadFile(previewFace.fileUrl, previewFace.fileName)}
      />
    );
  }

  return (
    <div className="PlaceDxfListPage-container">
      <Loading open={deleteFaceResult.loading || cloneFaceResult.loading} />
      {cloneDialog}
      {deleteDialog}
      {previewModal}
      <SectionHeader title="図面" actions={headerActions} />
      <ListSummary reorder={reorderMode} shownCount={faces.length} />
      {/* 取っ手の列があるかどうかで列の並びが違うので、列の幅は別々に保存する */}
      <TableScroll key={tableStorageKey} storageKey={tableStorageKey}>
        <table className="App-table">
          <thead>
            <tr>
              {handleHeader}
              <th>ファイル名</th>
              <th>サイズ</th>
              <th>マーカー</th>
              <th>写真</th>
              <th>作成日</th>
              <th>更新日</th>
              {actionHeader}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </TableScroll>
    </div>
  );
};
